import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ApiEndpoints } from "../../../helper/ApiEndpoints";
import ApiRequest from "../../../helper/ApiRequest";
import CustomSelect from "../../../component/CustomSelect";
import CustomImageUpload from "../../../component/CustomImageUpload";
import DynamicCKEditor from "../../../component/DynamicCkEditor";
import { toast } from "react-toastify";
import AdminHeader from "../../../component/AdminHeader";
import { BaseUrl } from "../../../helper/BaseUrl";

const editEndpoint = ApiEndpoints.project.edit;
const updateEndpoint = ApiEndpoints.project.update;

export default function EditProject() {
  const navigate = useNavigate();
  const { project_id } = useParams();

  const [projectTypes, setProjectTypes] = useState([]);

  const [previews, setPreviews] = useState({ thumbnail: null, background_thumbnail:null, images: [null] });
  const [postData, setPostData] = useState({ projecttype_id: "", title: "", slug: "", thumbnail: null, alt_thumbnail: "", background_thumbnail:null, alt_background_thumbnail:"", city: "", description: "", area: "", status: "0", rank: "", images: [{ image: null, alt_image: "" }, { image: null, alt_image: "" }] });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await ApiRequest("GET", editEndpoint + project_id);
        setPostData(response?.project || {});
        setProjectTypes(response?.project_type || []);

        setPreviews((prev) => ({
          ...prev,
          thumbnail: response?.project ? BaseUrl + response?.project?.thumbnail : prev?.thumbnail,
          background_thumbnail: response?.project ? BaseUrl + response?.project?.background_thumbnail : prev?.background_thumbnail,
          images: response.project.images?.map(img =>
            img.image ? BaseUrl + img.image : null
          ) || []
        }))
      } catch (error) {
        console.error("Failed to fetch categories:", error);
        setProjectTypes([]);
      }
    };

    fetchData();
  }, [project_id]);

  const handleImageChange = (index, file) => {
    const updatedImages = [...postData.images];
    updatedImages[index].image = file;

    setPostData(prev => ({
      ...prev,
      images: updatedImages
    }));

    const updatedPreviews = [...previews.images];
    updatedPreviews[index] = URL.createObjectURL(file);

    setPreviews(prev => ({
      ...prev,
      images: updatedPreviews
    }));
  };

  const handleAltChange = (index, value) => {
    const updatedImages = [...postData.images];
    updatedImages[index].alt_image = value;

    setPostData(prev => ({
      ...prev,
      images: updatedImages
    }));
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (files) {
      const file = files[0];
      // Update the previews state
      setPreviews((prev) => ({
        ...prev,
        [name]: URL.createObjectURL(file),
      }));

      // Update the postData state
      setPostData((prev) => ({
        ...prev,
        [name]: file,
      }));
    } else {
      // For non-file inputs, update the postData state directly
      setPostData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleEditorChange = (key, data) => {
    setPostData((prevState) => ({
      ...prevState,
      [key]: data,
    }));
  };

  const isEmptyValue = (value) => {
    if (value === null || value === "") return true;

    // allow File objects
    if (value instanceof File) return false;

    // arrays
    if (Array.isArray(value)) {
      return value.some(item => isEmptyValue(item));
    }

    // objects
    if (typeof value === "object") {
      return Object.values(value).some(v => isEmptyValue(v));
    }

    return false;
  };

  // Submit handler
  const handleSubmit = async (e) => {
    e.preventDefault();

    for (const [key, value] of Object.entries(postData)) {
      if (key === "location") continue;
      if (key === "year") continue;
      if (key === "client_name") continue;

      if (isEmptyValue(value)) {
        toast.error(`The field "${key}" is mandatory.`);
        return;
      }
    }

    const imagesPayload = postData.images.map((item, index) => ({
      image: item.image ? item.image.name || item.image : "",
      alt_image: item.alt_image
    }));

    const formData = new FormData();
    Object.entries(postData).forEach(([key, value]) => {
      if (key !== "images") {
        formData.append(key, value);
      }
      if (key === "images") {
        formData.append(key, JSON.stringify(imagesPayload))
      }
    });

    postData.images.forEach((item, index) => {
      if (item.image) {
        if (item.image instanceof File) {
          formData.append(`images[${index}][image]`, item.image);
        }
      }
    });

    try {
      const response = await ApiRequest("POST", updateEndpoint + project_id, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      if (response.error === false) {
        toast.success(response.message);
        navigate("/project");
      } else {
        toast.error(response.message)
      }
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  // =================Add More Functionality Start=====================

  const addImageBlock = () => {
    setPostData(prev => ({
      ...prev,
      images: [...prev.images, { image: null, alt_image: "" }]
    }));

    setPreviews(prev => ({
      ...prev,
      images: [...prev.images, null]
    }));
  };

  const removeImageBlock = (index) => {
    if (index === 0) return; // 🚫 first one protected

    setPostData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));

    setPreviews(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  // =================Add More Functionality End=====================

  const handleCheckboxChange = (e) => {
    setPostData((prev) => ({
      ...prev,
      status: e.target.checked ? "1" : "0",
    }));
  };

  const pagename = {
    name: 'Project Management',
    user: 'Admin',
    innerpage: 'Project Management / Project / Edit Project'
  }
  
  if(!postData){return null;}
  
  return (
    <>
      <AdminHeader pagename={pagename} />
      <div className="add-content-page">
        <form id="projectEdit" onSubmit={handleSubmit}>

          <div className="add-content-wrap">
            <div className="row">
              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Rank<small>*</small></label>
                  </div>
                  <input name="rank" type="number" className="form-control" value={postData.rank} onChange={handleChange} />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Status</label>
                  </div>
                  <label className="coustom-checkbox" style={{ marginTop: "14px" }}>
                    <input type="checkbox" checked={postData.status === "1"} onChange={handleCheckboxChange}/> {" "} <span className="checkmark"></span>Disable Project
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div className="add-content-wrap">
            <div className="row">
              <div className="col-lg-12">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>title<small>*</small></label>
                  </div>
                  <input value={postData.title} onChange={handleChange} name="title" type="text" className="form-control" placeholder="Enter Title" />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>slug<small>*</small></label>
                  </div>
                  <input value={postData?.slug} onChange={handleChange} name="slug" type="text" className="form-control" placeholder="Enter Slug" />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>city<small>*</small></label>
                  </div>
                  <input value={postData?.city} onChange={handleChange} name="city" type="text" className="form-control" placeholder="Enter city" />
                </div>
              </div>

              {/* <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>client name<small>*</small></label>
                  </div>
                  <input value={postData?.client_name} onChange={handleChange} name="client_name" type="text" className="form-control" placeholder="Enter client name" />
                </div>
              </div> */}

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>area<small>*</small></label>
                  </div>
                  <input value={postData?.area} onChange={handleChange} name="area" type="number" className="form-control" placeholder="Enter area" />
                </div>
              </div>

              {/* <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>year<small>*</small></label>
                  </div>
                  <input value={postData?.year} onChange={handleChange} name="year" type="number" className="form-control" placeholder="Enter year" />
                </div>
              </div> */}

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Project Type<small>*</small></label>
                  </div>
                  <CustomSelect defaultValue={postData?.projecttype_id} onChange={handleChange} name="projecttype_id" options={projectTypes} placeholder="Select Project Type" />
                </div>
              </div>
            </div>
          </div>

          <div className="add-content-wrap">
            <div className="row">

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Thumbnail Image Alt Tag<small>*</small></label>
                  </div>
                  <input value={postData.alt_thumbnail} onChange={handleChange} name="alt_thumbnail" type="text" className="form-control" placeholder="alt thumbnail" />
                </div>

                <div className="add-page-inputs input-add-image">
                  <div className="label-wrap-add">
                    <label>Thumbnail Image (712 × 786)<small>*</small></label>
                  </div>
                  <CustomImageUpload name="thumbnail"
                    // maxSize={550 * 1024}
                    acceptedFormats={[".webp"]}
                    onImageChange={handleChange}
                    imagePreview={previews.thumbnail} />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Background Thumbnail Image Alt Tag<small>*</small></label>
                  </div>
                  <input value={postData.alt_background_thumbnail} onChange={handleChange} name="alt_background_thumbnail" type="text" className="form-control" placeholder="alt background thumbnail" />
                </div>

                <div className="add-page-inputs input-add-image">
                  <div className="label-wrap-add">
                    <label>Background Thumbnail Image (1680 × 945 Px)<small>*</small></label>
                  </div>
                  <CustomImageUpload name="background_thumbnail"
                    // maxSize={550 * 1024}
                    acceptedFormats={[".webp"]}
                    onImageChange={handleChange}
                    imagePreview={previews.background_thumbnail} />
                </div>
              </div>

              <div className="col-lg-12">
                <div className="add-page-inputs">
                  <div className="label-wrap-add">
                    <label>Description<small>*</small></label>
                  </div>
                  <DynamicCKEditor data={postData.description} onChange={(event) => handleEditorChange("description", event?.editor?.getData())} />
                </div>
              </div>
            </div>
          </div>

          <div className="add-content-wrap">
            <div className="row">
              <h5>Gallery Image Section</h5>

              {postData.images.map((item, index) => (
                <div className="col-lg-6" key={index}>
                  <div className="add-page-inputs">

                    <div className="label-wrap-add">
                      <label>Gallery Image {index + 1} (778 X 746 px)<small>*</small></label>
                    </div>

                    <CustomImageUpload required name={`image_${index}`} acceptedFormats={[".webp"]} imagePreview={previews.images[index]} onImageChange={(e) => handleImageChange(index, e.target.files[0])} />

                    <div className="label-wrap-add">
                      <label>Alt Image {index + 1}<small>*</small></label>
                    </div>

                    <input type="text" className="form-control mt-2" placeholder="Alt image" value={item.alt_image} onChange={(e) => handleAltChange(index, e.target.value)} />

                    {index > 1 && (
                      <button type="button" className="btn btn-danger mt-2" onClick={() => removeImageBlock(index)}>
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              ))}

              <div className="col-lg-12 text-center mt-3">
                <button type="button" className="btn btn-success" onClick={addImageBlock}>
                  Add More Images
                </button>
              </div>
            </div>
          </div>

        </form>
        <button type="submit" form="projectEdit" className="default-btn">publish<span></span></button>
      </div>
    </>
  );
}
